import React, { useMemo, useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useToasts } from 'react-toast-notifications'
import { useHistory } from 'react-router-dom'
import { Modal, Spinner } from 'reactstrap'

import eventApi from '_api/event'
import { setPackingList, getListPackingList, changeNamePackingList, getMyEvents } from '_redux/modules/event'
import { checkIsEventClosed } from '_hooks/useIsEventClosed'
import useLoadingState from '_hooks/useLoadingState'

import './AddPackingListModal.scss'

export default function AddPackingListModal({ modal, toggle, listEvent }) {
  const { myEventForPackingList, listPackingList } = useSelector((state) => state.event)

  const dispatch = useDispatch()
  const history = useHistory()
  const { addToast } = useToasts()

  const [eventPicked, setEventPicked] = useState([])
  const [packingListName, setPackingListName] = useState('')
  const { isLoading, withLoading } = useLoadingState()

  useEffect(() => {
    if (modal) {
      setPackingListName('')
      setEventPicked([])
    }
  }, [modal])

  useEffect(() => {
    const initData = async () => {
      if (!listPackingList?.length) {
        await dispatch(getListPackingList)
      }
      if (!listEvent?.length && !myEventForPackingList?.length) {
        await dispatch(getMyEvents)
      }
    }
    initData()
  }, [])

  const mapListEvent = useMemo(() => {
    if (listPackingList?.length) {
      return listPackingList.flatMap((e) => e.eventIds || [])
    }
    return []
  }, [listPackingList])

  const eventsSource = listEvent || myEventForPackingList || []

  const availableEvents = useMemo(
    () => eventsSource.filter((e) => checkIsEventClosed(e) && !mapListEvent.includes(e.id)),
    [eventsSource, mapListEvent]
  )

  const handlePickedEvent = (event) => {
    setEventPicked((prev) => {
      if (prev.some((e) => e.id === event.id)) {
        return prev.filter((e) => e.id !== event.id)
      }
      return [...prev, event]
    })
  }

  const handleCreatePackingList = withLoading('createPackingList', async () => {
    if (!eventPicked.length) {
      addToast('No event has been selected yet.', { appearance: 'error', autoDismiss: true })
      return
    }

    const eventIds = eventPicked.map((e) => e.id)
    try {
      const { msgResp } = await eventApi.createPackingList({ eventIds })
      const trimmedName = packingListName.trim()

      if (trimmedName && msgResp?.id) {
        try {
          await eventApi.changeNamePackingList(msgResp.id, { packingListName: trimmedName })
          dispatch(changeNamePackingList(msgResp.id, trimmedName))
          msgResp.name = trimmedName
        } catch (nameError) {
          console.error('Failed to change packing list name:', nameError)
        }
      }

      await dispatch(setPackingList(msgResp))
      await dispatch(getListPackingList)
      toggle()
      history.push(`/event/packing-list?id=${msgResp.id}`)
    } catch (e) {
      addToast(e?.msgResp || 'Error creating packing list', {
        appearance: 'error',
        autoDismiss: true,
      })
    }
  })

  return (
    <Modal
      isOpen={modal}
      toggle={toggle}
      centered
      className='new-packing-list-modal'
    >
      <div className='modal-hd'>
        <span className='modal-title'>New Packing List</span>
        <button type='button' className='modal-close' onClick={toggle}>
          &times;
        </button>
      </div>

      <div className='modal-body'>
        <label className='field-label'>Packing List Name</label>
        <input
          className='name-input'
          type='text'
          placeholder='e.g. May 2026 Batch'
          value={packingListName}
          onChange={(e) => setPackingListName(e.target.value)}
        />

        <label className='field-label' style={{ marginBottom: '8px' }}>
          Select Events to Pack
        </label>

        <div className='event-check-list'>
          {availableEvents.length > 0 ? (
            availableEvents.map((item) => {
              const isPicked = eventPicked.some((e) => e.id === item.id)
              const isClosed = checkIsEventClosed(item)
              const ordersCount = item.totalOrderCount ?? 0

              return (
                <label key={item.id} className='event-check-item'>
                  <input
                    type='checkbox'
                    checked={isPicked}
                    onChange={() => handlePickedEvent(item)}
                  />
                  <div>
                    <div className='event-check-label'>{item.title}</div>
                    <div className='event-check-meta'>
                      ● {isClosed ? 'Closed' : 'Open'} · {ordersCount} {ordersCount === 1 ? 'order' : 'orders'}
                    </div>
                  </div>
                </label>
              )
            })
          ) : (
            <div className='no-event-to-pack'>
              <p>No closed events available to pack</p>
            </div>
          )}
        </div>

        <div className='modal-note'>
          <svg
            width='13'
            height='13'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
          >
            <circle cx='12' cy='12' r='10' />
            <line x1='12' y1='8' x2='12' y2='12' />
            <line x1='12' y1='16' x2='12.01' y2='16' />
          </svg>
          <span>Only closed events can be consolidated into a package.</span>
        </div>
      </div>

      <div className='modal-footer'>
        <button
          type='button'
          className='btn-create-list'
          onClick={handleCreatePackingList}
          disabled={!eventPicked.length || isLoading('createPackingList')}
        >
          {isLoading('createPackingList') ? <Spinner size='sm' /> : 'Create List'}
        </button>
      </div>
    </Modal>
  )
}
